import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import {
  DELETE_RESULT_TEMPLATE,
  UNASSIGN_RESULT_TEMPLATE_FROM_PROGRAMME,
} from '@/graphql/mutations/resultTemplate';
import type { ResultTemplate } from '@/types/composition-functions';
import { useSetProgrammesResultTemplateDefault } from '@/composables/mutations/tertiary/useProgrammeResultTemplateMutations';
import { extractGraphQLError } from '@/utils/graphqlError';

export function useResultTemplateMutations(options: { refetch: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { mutate: deleteTemplate } = useMutation(DELETE_RESULT_TEMPLATE);
  const { mutate: unassignTemplate } = useMutation(UNASSIGN_RESULT_TEMPLATE_FROM_PROGRAMME);

  const { setDefault } = useSetProgrammesResultTemplateDefault();

  const deletingId = ref<string | null>(null);

  /**
   * Stop a template being a programme's all-semesters default. Explicit
   * per-semester assignments are untouched; semesters without one fall back to
   * the active / previous semester's template.
   */
  function confirmRemoveDefault(
    template: ResultTemplate,
    programme: { id: string; program_name: string },
  ) {
    confirm.require({
      message: `Stop using "${template.name}" as the default for every semester of ${programme.program_name}? Semesters with their own assignment keep it.`,
      header: 'Remove Default Template',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      acceptLabel: 'Remove default',
      acceptClass: 'p-button-danger',
      accept: async() => {
        try {
          await setDefault({ programme_ids: [programme.id], result_template_id: null });
          toast.add({ severity: 'success', summary: 'Default removed', detail: `${programme.program_name} no longer defaults to "${template.name}".`, life: 4000 });
          options.refetch();
        } catch (e: unknown) {
          toast.add({ severity: 'error', summary: 'Could not remove default', detail: extractGraphQLError(e, 'Please try again.'), life: 5000 });
        }
      },
    });
  }

  function confirmUnassign(
    template: ResultTemplate,
    programme: { id: string; program_name: string },
    academicCalendarId: string | undefined,
  ) {
    if (!academicCalendarId) {
      toast.add({ severity: 'warn', summary: 'Select Calendar', detail: 'Please select an academic calendar first to unassign.', life: 4000 });
      return;
    }
    confirm.require({
      message: `Unassign template "${template.name}" from ${programme.program_name}? This can be re-assigned later.`,
      header: 'Unassign Template',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      acceptLabel: 'Unassign',
      acceptClass: 'p-button-danger',
      accept: async() => {
        try {
          const res = await unassignTemplate({
            programme_id: programme.id,
            academic_calendar_id: academicCalendarId,
          });
          const payload = res?.data?.unassignResultTemplateFromProgramme;
          toast.add({
            severity: payload?.success ? 'success' : 'error',
            summary: payload?.success ? 'Unassigned' : 'Error',
            detail: payload?.message || 'Done.',
            life: 4000,
          });
          options.refetch();
        } catch (e: unknown) {
          const message = e instanceof Error ? e.message : 'An unexpected error occurred';
          toast.add({ severity: 'error', summary: 'Cannot Unassign', detail: message, life: 5000 });
        }
      },
    });
  }

  function confirmDelete(template: ResultTemplate) {
    confirm.require({
      message: `Delete template "${template.name}"? This can't be undone.`,
      header: 'Delete Template',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      acceptLabel: 'Delete',
      acceptClass: 'p-button-danger',
      accept: async() => {
        deletingId.value = template.id;
        try {
          const res = await deleteTemplate({ id: template.id });
          const payload = res?.data?.deleteResultTemplate;
          if (payload && payload.success === false) {
            toast.add({ severity: 'error', summary: 'Error', detail: payload.message || 'Failed to delete template.', life: 4000 });
            return;
          }
          toast.add({ severity: 'success', summary: 'Deleted', detail: `"${template.name}" deleted.`, life: 3000 });
          options.refetch();
        } catch (e: unknown) {
          const message = e instanceof Error ? e.message : 'An unexpected error occurred';
          toast.add({ severity: 'error', summary: 'Could not delete', detail: message, life: 5000 });
        } finally {
          deletingId.value = null;
        }
      },
    });
  }

  return {
    deletingId,
    confirmUnassign,
    confirmRemoveDefault,
    confirmDelete,
  };
}
